import { useCallback, useEffect, useRef, useState } from 'react';
import { localStorageSafeGetItem, localStorageSafeSetItem } from '@zg-rentals/satellite-components';
import { appMarkerLogger } from '../bootstrap';

const logger = appMarkerLogger.child({ name: 'useMessagingWindowSize' });

export type MessagingWindowSize = { width: number; height: number };

type MessagingWindowResizeAxis = 'x' | 'y' | 'both';

// Matches the window's pre-resize hardcoded dimensions so the flag-off layout is the floor.
export const MESSAGING_WINDOW_MIN_SIZE: MessagingWindowSize = {
  width: 380,
  height: 480,
};
export const MESSAGING_WINDOW_SIZE_STORAGE_KEY = 'leadManagementMessagingWindowSize';
const KEYBOARD_RESIZE_STEP = 24;
export const VIEWPORT_MARGIN = 16;

// Arrow keys grow toward the top-left grip (Left/Up = larger) and shrink away from it.
const KEYBOARD_RESIZE_DELTAS: Record<string, Partial<MessagingWindowSize>> = {
  ArrowLeft: { width: KEYBOARD_RESIZE_STEP },
  ArrowRight: { width: -KEYBOARD_RESIZE_STEP },
  ArrowUp: { height: KEYBOARD_RESIZE_STEP },
  ArrowDown: { height: -KEYBOARD_RESIZE_STEP },
};

const UNBOUNDED_MAX: MessagingWindowSize = {
  width: Number.MAX_SAFE_INTEGER,
  height: Number.MAX_SAFE_INTEGER,
};

export function clampMessagingWindowSize(size: MessagingWindowSize, maxSize: MessagingWindowSize): MessagingWindowSize {
  // The viewport is a hard ceiling. When there's less room than the minimum — a short browser
  // window, or a phone in landscape — fitting the screen matters more than honoring the minimum,
  // otherwise the composer ends up below the fold with no way to scroll to it.
  const availableWidth = Math.max(maxSize.width, 0);
  const availableHeight = Math.max(maxSize.height, 0);
  // Whole pixels only: Chrome's device-emulation scaling reports fractional pointer coordinates,
  // and feeding those straight back in leaves the window on a half-pixel with blurry borders.
  return {
    width: Math.round(
      Math.min(Math.max(size.width, Math.min(MESSAGING_WINDOW_MIN_SIZE.width, availableWidth)), availableWidth),
    ),
    height: Math.round(
      Math.min(Math.max(size.height, Math.min(MESSAGING_WINDOW_MIN_SIZE.height, availableHeight)), availableHeight),
    ),
  };
}

// The window is anchored bottom-right, so the growable space is what's left of the viewport after
// the right-side reservation (drawer + spacing). The height ceiling is left to CSS instead of being
// computed here: `--ia-topnav-total-height` resolves to a `calc()` expression, which no amount of
// numeric parsing can turn into a pixel count, so the modal carries a `maxHeight` that subtracts
// the var directly (the same approach the RenterDetailsModal drawer backdrop uses for its `top`).
function getMaxSize(reservedRight: number): MessagingWindowSize {
  return {
    width: window.innerWidth - reservedRight - VIEWPORT_MARGIN,
    height: window.innerHeight - 2 * VIEWPORT_MARGIN,
  };
}

function isMessagingWindowSize(value: unknown): value is MessagingWindowSize {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as MessagingWindowSize).width === 'number' &&
    typeof (value as MessagingWindowSize).height === 'number'
  );
}

function readStoredSize(): MessagingWindowSize {
  const raw = localStorageSafeGetItem(MESSAGING_WINDOW_SIZE_STORAGE_KEY, '', logger);
  if (!raw) return MESSAGING_WINDOW_MIN_SIZE;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isMessagingWindowSize(parsed)) return parsed;
  } catch {
    logger.warn('Discarding unparseable stored messaging window size');
  }
  return MESSAGING_WINDOW_MIN_SIZE;
}

function storeSize(size: MessagingWindowSize) {
  localStorageSafeSetItem(MESSAGING_WINDOW_SIZE_STORAGE_KEY, JSON.stringify(size), logger);
}

// The DOM box is the truth at interaction start: CSS additionally caps the height with a top-nav
// `calc()` var the JS clamp can't see, so state can sit above what's painted, and starting the
// math there would put shrinks in a dead zone.
function measureRenderedSize(handle: HTMLElement): MessagingWindowSize | null {
  const windowElement = handle.closest('[data-messaging-window]');
  if (!windowElement) return null;
  const rect = windowElement.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;
  return { width: Math.round(rect.width), height: Math.round(rect.height) };
}

type DragState = {
  axis: MessagingWindowResizeAxis;
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
};

/**
 * Size state for the resizable messaging window. The window is bottom/right-anchored, so growing
 * from the top/left edges is pure width/height math — no position updates needed. The preferred
 * size persists to localStorage; the rendered size is additionally clamped to the space available
 * beside the drawer, so the preference survives the drawer opening and closing. `enabled` gates
 * the storage read and viewport listener so a flag-off render leaves no trace beyond defaults.
 */
export default function useMessagingWindowSize({
  reservedRight,
  enabled,
}: {
  reservedRight: number;
  enabled: boolean;
}) {
  const [preferredSize, setPreferredSize] = useState<MessagingWindowSize>(() =>
    enabled && typeof window !== 'undefined' ? readStoredSize() : MESSAGING_WINDOW_MIN_SIZE,
  );
  const [maxSize, setMaxSize] = useState<MessagingWindowSize>(() =>
    typeof window === 'undefined' ? UNBOUNDED_MAX : getMaxSize(reservedRight),
  );
  const dragStateRef = useRef<DragState | null>(null);
  const preferredSizeRef = useRef(preferredSize);
  preferredSizeRef.current = preferredSize;
  // Fallback starting point for drags/keys when the window element can't be measured: the rendered
  // (clamped) size, not the stored preference — a preference wider than the current viewport would
  // otherwise leave a dead zone before interactions take effect.
  const renderedSizeRef = useRef(preferredSize);
  const hasReadStoredSizeRef = useRef(enabled);

  // A late enable (viewport widened past the desktop breakpoint) still picks up the preference.
  useEffect(() => {
    if (!enabled || hasReadStoredSizeRef.current) return;
    hasReadStoredSizeRef.current = true;
    setPreferredSize(readStoredSize());
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const updateMaxSize = () => setMaxSize(getMaxSize(reservedRight));
    updateMaxSize();
    window.addEventListener('resize', updateMaxSize);
    return () => window.removeEventListener('resize', updateMaxSize);
  }, [enabled, reservedRight]);

  const size = clampMessagingWindowSize(preferredSize, maxSize);
  renderedSizeRef.current = size;

  const endDrag = useCallback(() => {
    if (!dragStateRef.current) return;
    dragStateRef.current = null;
    storeSize(preferredSizeRef.current);
  }, []);

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const drag = dragStateRef.current;
      if (!drag) return;
      // Pointer capture normally keeps every move and the release on this handle. When capture
      // isn't held, the release lands elsewhere and leaves the drag armed — so a later hover with
      // no button down would resize the window. Treat "no buttons pressed" as the release.
      if (event.buttons === 0) {
        endDrag();
        return;
      }
      const next = clampMessagingWindowSize(
        {
          width: drag.axis === 'y' ? drag.startWidth : drag.startWidth + (drag.startX - event.clientX),
          height: drag.axis === 'x' ? drag.startHeight : drag.startHeight + (drag.startY - event.clientY),
        },
        maxSize,
      );
      // Only the dragged axis is written to the preference: the drag seeds from the rendered
      // (possibly drawer-clamped) box, so writing the untouched axis would replace a stored
      // preference with the clamped value it's supposed to survive.
      setPreferredSize((previous) => ({
        width: drag.axis === 'y' ? previous.width : next.width,
        height: drag.axis === 'x' ? previous.height : next.height,
      }));
    },
    [maxSize, endDrag],
  );

  const getResizeHandleProps = useCallback(
    (axis: MessagingWindowResizeAxis) => ({
      onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
        event.preventDefault();
        try {
          event.currentTarget.setPointerCapture?.(event.pointerId);
        } catch {
          // jsdom and some older browsers lack pointer capture — degrade to capture-less dragging.
        }
        const startSize = measureRenderedSize(event.currentTarget) ?? renderedSizeRef.current;
        dragStateRef.current = {
          axis,
          startX: event.clientX,
          startY: event.clientY,
          startWidth: startSize.width,
          startHeight: startSize.height,
        };
      },
      onPointerMove: handlePointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      // Capture can be dropped mid-drag (element detached, browser cancels); persist what we have.
      onLostPointerCapture: endDrag,
    }),
    [handlePointerMove, endDrag],
  );

  const hasPendingKeyboardResizeRef = useRef(false);

  const handleResizeKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      const delta = KEYBOARD_RESIZE_DELTAS[event.key];
      if (!delta) return;
      event.preventDefault();
      const base = measureRenderedSize(event.currentTarget) ?? renderedSizeRef.current;
      const next = clampMessagingWindowSize(
        {
          width: base.width + (delta.width ?? 0),
          height: base.height + (delta.height ?? 0),
        },
        maxSize,
      );
      // Same per-axis rule as the pointer path: each arrow key touches exactly one axis, and the
      // other keeps its stored preference rather than the rendered (clamped) measurement.
      setPreferredSize((previous) => ({
        width: delta.width !== undefined ? next.width : previous.width,
        height: delta.height !== undefined ? next.height : previous.height,
      }));
      hasPendingKeyboardResizeRef.current = true;
    },
    [maxSize],
  );

  // Keydown auto-repeats while held; deferring the write here mirrors the pointer path's single
  // persist in endDrag. Wired to both keyup and blur — the keyup can land off the grip when focus
  // moves mid-press, and blur is the last event the grip is guaranteed to see.
  const persistKeyboardResize = useCallback(() => {
    if (!hasPendingKeyboardResizeRef.current) return;
    hasPendingKeyboardResizeRef.current = false;
    storeSize(preferredSizeRef.current);
  }, []);

  return { size, getResizeHandleProps, handleResizeKeyDown, persistKeyboardResize };
}
